/**
 * The floor map.
 *
 * Tables are drawn at their real geometry (`pos.x`, `pos.y`, `size`, `rotation`,
 * `shape`) inside a scaled canvas, so the screen matches the room. The colour
 * is NOT `tables.status` straight off the wire: it is recomputed locally with
 * `deriveTableStatus` from @sonauto/domain.
 *
 * WHY RECOMPUTE something the API already sent: `tables.status` is a cached
 * projection that several writers update concurrently (guest phone, POS, KDS).
 * The same pure function runs in the Worker, in the Durable Object and here, so
 * a POS that is one poll behind still paints the colour its own facts justify
 * instead of a stale cached one. It also means the floor map keeps working
 * offline off the last payload.
 */

import { useMemo, useState } from 'react';
import { deriveTableStatus } from '@sonauto/domain';
import type { TableStatusFacts } from '@sonauto/domain';
import { Badge, Button, EmptyState, ErrorState, LoadingState, statusLabelVi, toneFor } from '@sonauto/ui';
import type { Tone } from '@sonauto/ui';
import type { Area, FloorMapTable } from '../../lib/types';
import { vnd } from '../../lib/format';

const TONE_FILL: Record<Tone, string> = {
  neutral: 'bg-neutral-bg text-neutral-fg ring-neutral-solid/30',
  info: 'bg-info-bg text-info-fg ring-info-solid/40',
  success: 'bg-success-bg text-success-fg ring-success-solid/40',
  warning: 'bg-warning-bg text-warning-fg ring-warning-solid/50',
  danger: 'bg-danger-bg text-danger-fg ring-danger-solid/50',
  busy: 'bg-busy-bg text-busy-fg ring-busy-solid/40',
  accent: 'bg-accent-bg text-accent-fg ring-accent-solid/40',
};

/**
 * Wire row → the facts `deriveTableStatus` needs.
 *
 * Three facts the floor-map payload genuinely cannot carry are derived from the
 * status the server already computed, rather than invented:
 *   - `hasOpenCallStaffRequest` / `hasOpenPaymentRequest`: the payload has no
 *     service-request list, so the server's own HELP_REQUESTED /
 *     PAYMENT_REQUESTED status is taken as the fact;
 *   - `isGuestOrdering`: presence of a guest device is realtime-only data that
 *     no REST payload carries.
 * Everything else is a real column.
 */
export function factsFor(table: FloorMapTable): TableStatusFacts {
  const session = table.session;
  return {
    isActive: table.isActive,
    outOfService: table.status === 'OUT_OF_SERVICE',
    locked: table.status === 'LOCKED' || session?.status === 'LOCKED',
    isSettingUp: table.status === 'SETTING_UP',
    needsCleaning: table.status === 'NEEDS_CLEANING',
    hasReservation: table.status === 'RESERVED',
    hasOpenSession: session !== null,
    isGuestOrdering: session?.status === 'ORDERING',
    pendingConfirmationOrderCount: session?.status === 'WAITING_CONFIRMATION' ? 1 : 0,
    unservedItemCount: session?.unservedItemCount ?? 0,
    // The payload has no served-item count. `total > 0` with nothing unserved
    // is the only honest proxy for "food has landed".
    servedItemCount: session !== null && session.unservedItemCount === 0 && session.totalMinor > 0 ? 1 : 0,
    hasOpenCallStaffRequest: table.status === 'HELP_REQUESTED',
    hasOpenPaymentRequest: table.status === 'PAYMENT_REQUESTED' || session?.status === 'PAYMENT_REQUESTED',
    paymentInProgress: table.status === 'PAYMENT_PROCESSING' || session?.status === 'PAYMENT_PROCESSING',
  };
}

export interface FloorMapProps {
  readonly tables: readonly FloorMapTable[];
  readonly areas: readonly Area[];
  readonly loading: boolean;
  readonly error: string | null;
  readonly onRetry: () => void;
  readonly onSelect: (table: FloorMapTable) => void;
  /** Multi-select is used by table merge/transfer flows. */
  readonly selectedIds?: ReadonlySet<string>;
}

export function FloorMap({
  tables,
  areas,
  loading,
  error,
  onRetry,
  onSelect,
  selectedIds,
}: FloorMapProps): React.JSX.Element {
  const [areaId, setAreaId] = useState<string>('');

  const visible = useMemo(
    () => (areaId === '' ? tables : tables.filter((t) => t.areaId === areaId)),
    [areaId, tables],
  );

  const bounds = useMemo(() => {
    let maxX = 600;
    let maxY = 400;
    for (const table of visible) {
      maxX = Math.max(maxX, table.pos.x + table.size.width + 40);
      maxY = Math.max(maxY, table.pos.y + table.size.height + 40);
    }
    return { width: maxX, height: maxY };
  }, [visible]);

  if (loading && tables.length === 0) return <LoadingState label="Đang tải sơ đồ bàn…" />;
  if (error !== null && tables.length === 0) {
    return <ErrorState title="Không tải được sơ đồ bàn" message={error} onRetry={onRetry} />;
  }
  if (tables.length === 0) {
    return <EmptyState title="Chi nhánh chưa có bàn nào" description="Thêm bàn trong ứng dụng Quản trị." />;
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-wrap items-center gap-2 border-b border-line bg-surface px-3 py-2">
        <Button variant={areaId === '' ? 'primary' : 'secondary'} size="sm" onClick={() => setAreaId('')}>
          Tất cả ({tables.length})
        </Button>
        {areas.map((area) => (
          <Button
            key={area.id}
            variant={areaId === area.id ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => setAreaId(area.id)}
          >
            {area.name}
          </Button>
        ))}
        {areas.length === 0 ? (
          <span className="text-xs text-fg-subtle">
            Không đọc được khu vực (cần quyền <code className="font-mono">area.view</code>) — hiển thị toàn bộ bàn.
          </span>
        ) : null}
        <div className="ml-auto flex flex-wrap gap-1">
          {(['EMPTY', 'OCCUPIED', 'WAITING_FOOD', 'PAYMENT_REQUESTED', 'NEEDS_CLEANING'] as const).map((status) => (
            <Badge key={status} tone={toneFor(status)} size="sm" dot>
              {statusLabelVi(status)}
            </Badge>
          ))}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-auto bg-surface-sunken p-4">
        <div className="relative" style={{ width: bounds.width, height: bounds.height }}>
          {visible.map((table) => {
            const derived = deriveTableStatus(factsFor(table));
            const tone = toneFor(derived);
            const selected = selectedIds?.has(table.id) === true;
            return (
              <button
                key={table.id}
                type="button"
                onClick={() => onSelect(table)}
                style={{
                  left: table.pos.x,
                  top: table.pos.y,
                  width: table.size.width,
                  height: table.size.height,
                  transform: `rotate(${table.rotation}deg)`,
                }}
                className={[
                  'absolute flex flex-col items-center justify-center gap-0.5 p-1 text-center ring-2 transition',
                  'focus:outline-none focus-visible:ring-4 focus-visible:ring-ring',
                  table.shape === 'ROUND' ? 'rounded-full' : 'rounded-md',
                  TONE_FILL[tone],
                  selected ? 'ring-4 ring-brand-600' : '',
                  table.isActive ? '' : 'opacity-50',
                ].join(' ')}
                title={`${table.displayName} — ${statusLabelVi(derived)}`}
              >
                <span className="text-flow text-sm font-bold leading-tight">{table.displayName}</span>
                <span className="text-[10px] leading-tight opacity-80">{table.seats} chỗ</span>
                {table.session !== null ? (
                  <span className="text-[10px] font-semibold leading-tight">
                    {vnd(table.session.totalMinor)}
                    {table.session.unservedItemCount > 0 ? ` · ${table.session.unservedItemCount} món chờ` : ''}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
